import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { qualityMachine } from '@/config/qualityStates';
import { useQualityDetailContext } from '@/contexts/QualityContext';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';

import { useNotificationModal } from './useNotificationModal';

const parseUserList = (list = []) => {
  return (list || [])
    .map((item) => {
      if (typeof item === 'string') return item;
      return item?.value?.sub || item?.sub;
    })
    .filter(Boolean);
};

const toBooleanFlag = (value) => value === true || value === 1 || value === '1';

const payloadQuality = (form, creator) => {
  const reviewers = {
    managementApprovers: parseUserList(form.reviewers?.managementApprovers),
    processOwners: parseUserList(form.reviewers?.processOwners),
    qmrQa: parseUserList(form.reviewers?.qmrQa),
  };
  const reviewerPipeline = qualityMachine.buildPipeline(reviewers);

  return {
    informationDescription: form.informationDescription,
    informationDetails: form.informationDetails,
    informationFile: form.informationFile,
    informationRemark: form.informationRemark,
    nonconformitySpecification: form.nonconformitySpecification,
    nonconformityQms: form.nonconformityQms,
    nonconformityRegulations: form.nonconformityRegulations,
    nonconformitySafety: form.nonconformitySafety,
    nonconformityDetails: {
      statement: {
        text: form.nonconformityDetails?.statement?.text || '',
        attachments:
          form.nonconformityDetails?.statement?.attachments || [],
      },
      rootCause: {
        text: form.nonconformityDetails?.rootCause?.text || '',
      },
    },
    planChecked: toBooleanFlag(form.planChecked),
    planActionReason: form.planActionReason,
    planActions: form.planActions,
    planRequired: toBooleanFlag(form.planRequired),
    planECReason: form.planECReason,
    planCriteriaEffectiveness: form.planCriteriaEffectiveness,
    planCriteriaQA: form.planCriteriaQA,
    planFile: form.planFile,
    reviewers,
    reviewerPipeline,

    creator: creator?.sub,
  };
};

const withUploadedAttachments = async (payload, form) => {
  const pending =
    form.nonconformityDetails?.statement?.pendingAttachments || [];

  if (!pending.length) return payload;

  const uploaded = await Promise.all(
    pending.map((file) => apiService.uploadFile(file))
  );

  return {
    ...payload,
    nonconformityDetails: {
      ...payload.nonconformityDetails,
      statement: {
        ...payload.nonconformityDetails.statement,
        attachments: [
          ...payload.nonconformityDetails.statement.attachments,
          ...uploaded,
        ],
      },
    },
  };
};

const REQUIRED_APPROVAL_GROUPS = [
  ['qmrQa', 'QMR/QA reviewer'],
  ['processOwners', 'Process Owner reviewer'],
  ['managementApprovers', 'Management approver'],
];

const useQualityActions = () => {
  const navigate = useNavigate();

  const { form, id: contextId, reviews = [] } = useQualityDetailContext();
  const id = form.id || contextId;

  const { showNotificationModal } = useNotificationModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);

  const validateApprovals = (reviewers) => {
    const missingGroups = REQUIRED_APPROVAL_GROUPS.filter(
      ([key]) => !reviewers?.[key]?.length
    ).map(([, label]) => label);

    if (!missingGroups.length) return true;

    showNotificationModal(
      'Cannot Submit',
      `Please assign at least one ${missingGroups.join(', ')} before submitting.`,
      'error'
    );

    return false;
  };

  const validateActionPlans = () => {
    if (Number(form.planChecked) !== 1) return true;

    const planActions = form.planActions || [];
    const hasMissingActionPlanFields =
      planActions.length === 0 ||
      planActions.some(
        ({ account, description, implementAt }) =>
          !description?.trim() || !account || !implementAt
      );

    if (!hasMissingActionPlanFields) return true;

    showNotificationModal(
      'Cannot Submit',
      'Please complete the description, responsible person, and target implementation date for each action plan.',
      'error'
    );

    return false;
  };

  const validateNonconformityDetails = () => {
    const hasNonconformity = [
      form.nonconformitySpecification,
      form.nonconformityQms,
      form.nonconformityRegulations,
      form.nonconformitySafety,
    ].some(({ check } = {}) => String(check) === '1');

    if (!hasNonconformity) return true;

    const statement = form.nonconformityDetails?.statement?.text?.trim();
    const rootCause = form.nonconformityDetails?.rootCause?.text?.trim();

    if (statement && rootCause) return true;

    showNotificationModal(
      'Cannot Submit',
      'Please complete the nonconformity statement and root cause before submitting.',
      'error'
    );

    return false;
  };

  const createQuality = async () => {
    if (submitLock.current) return;

    try {
      submitLock.current = true;
      setIsSubmitting(true);

      const creator = getUserInfo();
      let payload = payloadQuality(form, creator);

      if (!validateApprovals(payload.reviewers)) return;
      if (!validateActionPlans()) return;
      if (!validateNonconformityDetails()) return;
      payload = await withUploadedAttachments(payload, form);

      const response = await apiService.qualities.create({ ...payload });

      if (response.data.success) {
        navigate('/qualities', { state: 'pending' });

        showNotificationModal(
          'Submitted',
          `Quality event submitted successfully. Quality event number: ${response.data.data?.serial || response.data.data?.id}`,
          'success'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to submit quality event'
        );
    } catch (error) {
      console.error('Error submitting quality event:', error);

      showNotificationModal(
        'Submit Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to submit quality event',
        'error'
      );
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const createDraft = async () => {
    try {
      const creator = getUserInfo();
      let payload = payloadQuality(form, creator);
      payload = await withUploadedAttachments(payload, form);

      const response = await apiService.qualities.create({
        ...payload,
        isDraft: true,
      });

      if (response.data.success) {
        navigate('/qualities', { state: 'pending' });
        showNotificationModal(
          'Draft Saved',
          `Quality event saved as draft successfully. Quality event number: ${response.data.data?.serial || response.data.data?.id}`,
          'success'
        );
      } else
        throw new Error(response.data.error || 'Failed to draft quality event');
    } catch (error) {
      console.error('Error drafting quality event:', error);

      showNotificationModal(
        'Draft Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to draft quality event',
        'error'
      );
    }
  };

  const saveDraft = async () => {
    try {
      const creator = getUserInfo();
      let payload = payloadQuality(form, creator);
      payload = await withUploadedAttachments(payload, form);

      const response = await apiService.qualities.update(id, payload);

      if (response.data.success) {
        showNotificationModal(
          'Draft Saved',
          `Quality event saved as draft successfully.`,
          'success'
        );
        navigate('/qualities', { state: 'pending' });
      } else throw new Error(response.data.error || 'Failed to save draft');
    } catch (error) {
      console.error('Error saving draft:', error);

      showNotificationModal(
        'Save Failed',
        error.response?.data?.error || error.message || 'Failed to save draft',
        'error'
      );
    }
  };

  const deleteDraft = async () => {
    try {
      const response = await apiService.qualities.delete(id);

      if (response.data.success) {
        navigate('/qualities', { state: 'pending' });
        showNotificationModal(
          'Draft Deleted',
          'Quality event draft deleted successfully.',
          'success'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to delete quality event draft'
        );
    } catch (error) {
      console.error('Error deleting quality event draft:', error);

      showNotificationModal(
        'Delete Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to delete quality event draft',
        'error'
      );
    }
  };

  const submitDraft = async () => {
    if (submitLock.current) return;

    try {
      submitLock.current = true;
      setIsSubmitting(true);

      const creator = getUserInfo();
      let payload = payloadQuality(form, creator);

      if (!validateApprovals(payload.reviewers)) return;
      if (!validateActionPlans()) return;
      if (!validateNonconformityDetails()) return;
      payload = await withUploadedAttachments(payload, form);

      const response = await apiService.qualities.submit(id, {
        nonconformitySpecification: payload.nonconformitySpecification,
        nonconformityQms: payload.nonconformityQms,
        nonconformityRegulations: payload.nonconformityRegulations,
        nonconformitySafety: payload.nonconformitySafety,
        nonconformityDetails: payload.nonconformityDetails,
        reviewerPipeline: payload.reviewerPipeline,
        reviewers: payload.reviewers,
      });

      if (response.data.success) {
        navigate('/qualities', { state: 'pending' });
        showNotificationModal(
          'Submitted',
          'Quality event submitted successfully.',
          'success'
        );
      } else
        throw new Error(
          response.data.error || 'Failed to submit quality event'
        );
    } catch (error) {
      console.error('Error submitting quality event:', error);

      showNotificationModal(
        'Submit Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to submit quality event',
        'error'
      );
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  const approve = async () => {
    try {
      const reviewerPipeline =
        form.reviewerPipeline || qualityMachine.buildPipeline(form.reviewers);
      const resolved = qualityMachine.resolve(
        form.status,
        'approve',
        form.currentActorIndex ?? 0,
        reviewerPipeline.length
      );

      if (!resolved) throw new Error('Failed to resolve quality approval');

      const response = await apiService.qualities.approve(id, {
        ...form,
        ...resolved,
        reviewerPipeline,
      });

      if (response.data.success) {
        showNotificationModal('Approved', 'Quality event approved.', 'success');
        navigate('/qualities', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to approve quality event'
        );
    } catch (error) {
      console.error('Error approving quality event:', error);

      showNotificationModal(
        'Approve Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to approve quality event',
        'error'
      );
    }
  };

  const reject = async (reason) => {
    try {
      const reviewerPipeline =
        form.reviewerPipeline || qualityMachine.buildPipeline(form.reviewers);

      const response = await apiService.qualities.reject(id, {
        ...form,
        reviewerPipeline,
        reason,
        rejectedBy: getUserInfo()?.sub,
      });

      if (response.data.success) {
        showNotificationModal('Rejected', 'Quality event rejected.', 'info');
        navigate('/qualities', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to reject quality event'
        );
    } catch (error) {
      console.error('Error rejecting quality event:', error);

      showNotificationModal(
        'Reject Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to reject quality event',
        'error'
      );
    }
  };

  const returnToRequestor = async () => {
    try {
      const { sub, name } = getUserInfo();

      for (const { tab, text, entityId: commentEntityId } of reviews) {
        if (commentEntityId) continue;

        await apiService.comments.create({
          entityType: 'QualityEvent',
          entityId: id,
          entityRevision: form.revision,
          tab,
          text,
          authorSub: sub,
          authorName: name,
        });
      }

      await apiService.comments.bulkFreeze({
        entityType: 'QualityEvent',
        entityId: id,
        entityRevision: form.revision,
      });

      const reviewerPipeline =
        form.reviewerPipeline || qualityMachine.buildPipeline(form.reviewers);
      const response = await apiService.qualities.returnQe(id, {
        ...form,
        comments: reviews,
        reviewerPipeline,
      });

      if (response.data.success) {
        showNotificationModal(
          'Returned',
          'Comments sent to requestor.',
          'info'
        );
        navigate('/qualities', { state: 'pending' });
      } else
        throw new Error(
          response.data.error || 'Failed to return quality event'
        );
    } catch (error) {
      console.error('Error returning quality event:', error);

      showNotificationModal(
        'Return Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to return quality event',
        'error'
      );
    }
  };

  return {
    approve,
    createQuality,
    createDraft,
    deleteDraft,
    saveDraft,
    isSubmitting,
    reject,
    returnToRequestor,
    submitDraft,
  };
};

export default useQualityActions;
